import {CloseButton as ClearSearch, Input, InputGroup, InputRightElement, Spinner} from '@chakra-ui/react';
import {CSSObject} from '@emotion/react';
import {colors} from 'components/providers';
import {useDebounce} from 'hooks/useDebounce';
import {FC, PropsWithChildren, useEffect, useState} from 'react';

interface IProps {
  isLoading?: boolean;
  searchValue: string;
  placeholder: string;
  onChangeValueWithDebounce: (value: string) => void;
  onClearSearchField: () => void;
  cssPropsForInput: CSSObject;
  delay?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export const InputSearchWithDebounce: FC<PropsWithChildren<IProps>> = ({
  isLoading,
  searchValue,
  placeholder,
  onChangeValueWithDebounce,
  onClearSearchField,
  cssPropsForInput,
  delay,
  children,
  onKeyDown,
  inputRef,
}) => {
  const [value, setValue] = useState(searchValue);

  useEffect(() => {
    setValue((prevState) => (prevState !== searchValue ? searchValue : prevState));
  }, [searchValue]);

  const onChangeValueHandler = (value: string) => {
    onChangeValueWithDebounce(value);
  };

  /**
   * Debounce like lodash.debounce not working in fcc
   * Below is working version
   * @see-https://stackoverflow.com/questions/47809666/lodash-debounce-not-working-in-react
   * @see-https://hackernoon.com/how-to-use-debounce-in-nextjs
   */
  const debounce = useDebounce({callback: onChangedata, delay: delay});
  function onChangedata(value: string) {
    onChangeValueHandler(value);
  }

  const onChangeValue = (value: string) => {
    setValue(value);
    debounce(value);
  };

  return (
    <InputGroup isTruncated h="full">
      {(isLoading || searchValue) && (
        <InputRightElement mr="10px" h="full">
          {isLoading && <Spinner color={colors.pink} />}
          {searchValue && !isLoading && (
            <ClearSearch
              sx={{
                bg: 'transparent',
                _hover: {
                  bg: 'transparent',
                },
                _focus: {border: 'none'},
                _active: {
                  bg: 'transparent',
                },
              }}
              position="absolute"
              right="15px"
              h="full"
              onClick={onClearSearchField}
            />
          )}
        </InputRightElement>
      )}
      {children}
      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder={placeholder}
        bg={colors.white}
        borderRadius="1rem"
        color={colors.black}
        fontSize="1rem"
        h="45px"
        border="1px solid"
        textOverflow="ellipsis"
        _placeholder={{
          fontWeight: 600,
        }}
        sx={
          {
            _placeholder: {
              color: colors.textGray,
            },
            pr: '35px',
            _focus: {
              boxShadow: 'none',
            },
            ...cssPropsForInput,
          } as any
        }
        onKeyDown={onKeyDown}
      />
    </InputGroup>
  );
};
